import Service, { service } from '@ember/service';
import { format, formatISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import { SERVICE_REQUEST_TIMEOUT_MS } from 'garaje/utils/catch-service-error';
import type { LENDING_STATUS } from 'garaje/utils/enums';
import urlBuilder from 'garaje/utils/url-builder';
import type { ValueOf } from 'type-fest';

export interface DeskLendingDateStatus {
  date: string;
  lendingStatus: ValueOf<typeof LENDING_STATUS>;
  reservation?: {
    reservationId: number;
    bookedByEmployeeId: number;
    bookedByName: string;
    bookedByEmail: string;
    startTime: string;
    endTime: string;
  } | null;
}

export interface DeskLendingStatusResponse {
  deskId: number;
  deskName: string | null;
  floorId: number | null;
  floorName: string | null;
  dates: DeskLendingDateStatus[];
}

export interface LendDeskResponse {
  lentDates: string[];
  assignmentsCreated: number;
}

export interface UnlendDeskResponse {
  unlentDates: string[];
  cancelledReservations: { reservationId: number; employeeId: number }[];
}

export interface MyLentDeskResponse {
  deskId: number;
}

export interface LendableDeskInfo {
  deskId: number;
  deskName: string;
  floorName: string;
  dates: DeskLendingDateStatus[];
}

export interface MyLendableDesksResponse {
  desks: LendableDeskInfo[];
}

const toIsoDate = (date: Date): string => formatISO(date, { representation: 'date' });

export default class DeskLendingService extends Service {
  @service declare ajaxFetch: AjaxFetchService;

  async lendDesk(deskId: string, date: Date): Promise<LendDeskResponse> {
    const formattedDate = format(date, 'yyyy-MM-dd');
    const url = urlBuilder.rms.lendDesk(deskId);

    return this.ajaxFetch.post<LendDeskResponse>(url, {
      contentType: 'application/json',
      data: { dates: [formattedDate] },
    });
  }

  async unlendDesk(deskId: string, date: Date): Promise<UnlendDeskResponse> {
    const formattedDate = format(date, 'yyyy-MM-dd');
    const url = urlBuilder.rms.unlendDesk(deskId);

    return this.ajaxFetch.post<UnlendDeskResponse>(url, {
      contentType: 'application/json',
      data: { dates: [formattedDate] },
    });
  }

  async lendDeskBulk(deskId: string, dates: string[]): Promise<LendDeskResponse> {
    const url = urlBuilder.rms.lendDesk(deskId);

    return this.ajaxFetch.post<LendDeskResponse>(url, {
      contentType: 'application/json',
      data: { dates },
    });
  }

  async fetchMyLentDesk(locationId: string): Promise<MyLentDeskResponse | null> {
    const url = urlBuilder.rms.myLentDesk(locationId);
    try {
      const response = await this.ajaxFetch.request<MyLentDeskResponse | undefined>(url, {
        timeout: SERVICE_REQUEST_TIMEOUT_MS,
      });
      return response ?? null;
    } catch {
      return null;
    }
  }

  async fetchMyLendableDesks(locationId: string, startDate: Date, endDate: Date): Promise<MyLendableDesksResponse> {
    const start = format(startDate, 'yyyy-MM-dd');
    const end = format(endDate, 'yyyy-MM-dd');
    const url = urlBuilder.rms.myLendableDesks(locationId, start, end);

    return this.ajaxFetch.request<MyLendableDesksResponse>(url, { timeout: SERVICE_REQUEST_TIMEOUT_MS });
  }

  // The window dates here mean "calendar days at the workspace's physical
  // location" (e.g. Mon–Fri at the LA office), not "absolute UTC instants" —
  // an admin in Tokyo asking about LA's today must hit the LA-today row even
  // though their browser-local date is already tomorrow. Pass the location TZ
  // so we resolve the keys against location wall-clock rather than browser TZ.
  async fetchLendingStatus(
    deskId: string,
    startDate: Date,
    endDate: Date,
    locationTimeZone: string,
  ): Promise<DeskLendingStatusResponse> {
    const start = formatInTimeZone(startDate, locationTimeZone, 'yyyy-MM-dd');
    const end = formatInTimeZone(endDate, locationTimeZone, 'yyyy-MM-dd');
    const url = urlBuilder.rms.lendingStatus(deskId, start, end);

    return this.ajaxFetch.request<DeskLendingStatusResponse>(url, { timeout: SERVICE_REQUEST_TIMEOUT_MS });
  }

  async adminLendDesk(deskId: string, dates: Date[], onBehalfOfEmployeeId: number): Promise<LendDeskResponse> {
    const url = urlBuilder.rms.lendDesk(deskId);

    return this.ajaxFetch.post<LendDeskResponse>(url, {
      contentType: 'application/json',
      data: {
        dates: dates.map(toIsoDate),
        onBehalfOfEmployeeId,
      },
    });
  }

  async adminCancelLending(deskId: string, dates: Date[], onBehalfOfEmployeeId: number): Promise<UnlendDeskResponse> {
    const url = urlBuilder.rms.unlendDesk(deskId);

    return this.ajaxFetch.post<UnlendDeskResponse>(url, {
      contentType: 'application/json',
      data: {
        dates: dates.map(toIsoDate),
        onBehalfOfEmployeeId,
        failIfBooked: true,
      },
    });
  }

  async adminCancelReservation(reservationId: number): Promise<void> {
    const url = urlBuilder.rms.cancelReservationByAdmin(reservationId);

    await this.ajaxFetch.post<void>(url, {
      contentType: 'application/json',
    });
  }
}

declare module '@ember/service' {
  interface Registry {
    deskLending: DeskLendingService;
  }
}
